import React from "react";
import { CalendarCheck, CheckCircle2, Pencil } from "lucide-react";

// What we'd suggest, and why. Everything here is already ticked in the list
// below it, so the visitor reviews rather than re-chooses.
export default function Recommendations({ help, services, ids, reasons, requiredNames = [], standardNames = [], onRedo, onDiscovery }) {
  const chosen = services.filter((s) => ids.includes(s.service_id));
  if (chosen.length === 0) return null;

  return (
    <div className="rounded-2xl border border-orange/30 bg-orange/[0.04] p-5 md:p-6" data-testid="pricing-recommendations">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-extrabold text-navy md:text-xl">
            {help.results_heading || "Here's where we'd suggest starting"}
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
            {help.results_intro ||
              "Based on what you've told us, these are the services we think could be relevant to your business. You can review and change your selection before continuing."}
          </p>
        </div>
        <button
          type="button"
          onClick={onRedo}
          data-testid="pricing-recommendations-redo"
          className="inline-flex shrink-0 items-center gap-1.5 text-xs font-bold text-orange duration-200 [transition-property:color] hover:text-orange-dark"
        >
          <Pencil size={11} /> Change my answers
        </button>
      </div>

      <ul className="mt-5 space-y-3.5">
        {chosen.map((s) => (
          <li key={s.service_id} className="flex items-start gap-3" data-testid={`pricing-recommendation-${s.service_id}`}>
            <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-orange" />
            <div className="min-w-0">
              <p className="text-sm font-bold text-navy">{s.name}</p>
              {reasons[s.service_id] && (
                <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{reasons[s.service_id]}</p>
              )}
            </div>
          </li>
        ))}
      </ul>

      {requiredNames.length > 0 && (
        <p className="mt-4 text-xs leading-relaxed text-slate-500" data-testid="pricing-recommendations-required">
          Required with your selection: <strong className="text-navy">{requiredNames.join(", ")}</strong>.
        </p>
      )}

      {standardNames.length > 0 && (
        <p className="mt-2 text-xs leading-relaxed text-slate-500" data-testid="pricing-recommendations-standard">
          Included as standard: <strong className="text-navy">{standardNames.join(", ")}</strong>.
        </p>
      )}

      <div className="mt-5 border-t border-orange/20 pt-4">
        <p className="text-xs leading-relaxed text-slate-600">
          {help.unsure_text ||
            "Still not sure what support you need? We can talk through your business and help you work out the right approach."}
        </p>
        <button
          type="button"
          onClick={onDiscovery}
          data-testid="pricing-recommendations-discovery"
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-xs font-bold text-navy duration-200 [transition-property:border-color] hover:border-navy"
        >
          <CalendarCheck size={13} /> {help.unsure_label || "Request a Discovery Call"}
        </button>
      </div>
    </div>
  );
}
